// ** Next Imports

// ** Emotion Imports
import { CacheProvider } from '@emotion/react';
import Head from 'next/head';
import { Router } from 'next/router';
// ** Loader Import
import NProgress from 'nprogress';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
// ** Store Imports
import { store } from 'src/store';

// ** Config Imports
import 'src/configs/i18n';

// ** Third Party Import
import { Toaster } from 'react-hot-toast';
import UnifiedGuard from 'src/@core/components/auth/UnifiedGuard';
import {
  SettingsConsumer,
  SettingsProvider,
} from 'src/@core/context/settingsContext';
// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast';
import ThemeComponent from 'src/@core/theme/ThemeComponent';
// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { defaultACLObj } from 'src/configs/acl';
import themeConfig from 'src/configs/themeConfig';
import { initializeAccessibilityFixes } from 'src/utils/accessibility';
// ** Contexts
import { AuthProvider } from 'src/context/AuthContext';
// ** Component Imports
import UserLayout from 'src/layouts/UserLayout';

// ** Lazy load heavy libraries
// Removed Prismjs - not being used in the application

// ** Removed React Perfect Scrollbar CSS import - no longer needed

// ** Iconify icons are now loaded on-demand via @iconify/react
// No need to preload the entire bundle

// ** Global css styles
import '../../styles/globals.css';

import 'swiper/swiper-bundle.css';

/* import DynamicHead from 'src/components/DynamicHead'; */

const clientSideEmotionCache = createEmotionCache();

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start();
  });
  Router.events.on('routeChangeError', () => {
    NProgress.done();
  });
  Router.events.on('routeChangeComplete', () => {
    NProgress.done();
  });
}

// ** Configure JSS & ClassName
const App = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  // Initialize accessibility fixes on mount
  useEffect(() => {
    initializeAccessibilityFixes();
  }, []);

  // Variables
  const contentHeightFixed = Component.contentHeightFixed ?? false;

  const getLayout =
    Component.getLayout ??
    ((page) => (
      <UserLayout contentHeightFixed={contentHeightFixed}>{page}</UserLayout>
    ));
  const setConfig = Component.setConfig ?? undefined;
  const authGuard = Component.authGuard ?? true;
  const guestGuard = Component.guestGuard ?? false;
  const aclAbilities = Component.acl ?? defaultACLObj;

  /* const ogTitle = pageProps.ogTitle || `${themeConfig.templateName} - Fund Raising System`;
  const ogDescription = pageProps.ogDescription || `${themeConfig.templateName} – Fund Raising System.`;
  const ogImage = pageProps.ogImage; */

  return (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <meta content="initial-scale=1, width=device-width" name="viewport" />
        </Head>
        <ErrorBoundary>
          <AuthProvider>
            <SettingsProvider
              {...(setConfig ? { pageSettings: setConfig() } : {})}
            >
              <SettingsConsumer>
                {({ settings }) => {
                  return (
                    <ThemeComponent settings={settings}>
                      <UnifiedGuard
                        aclAbilities={aclAbilities}
                        authGuard={authGuard}
                        guestGuard={guestGuard}
                      >
                        {getLayout(<Component {...pageProps} />)}
                      </UnifiedGuard>
                      <ReactHotToast>
                        <Toaster
                          position={settings.toastPosition}
                          toastOptions={{ className: 'react-hot-toast' }}
                        />
                      </ReactHotToast>
                    </ThemeComponent>
                  );
                }}
              </SettingsConsumer>
            </SettingsProvider>
          </AuthProvider>
        </ErrorBoundary>
      </CacheProvider>
    </Provider>
  );
};

export default App;
